import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppActionMenu from '../../components/AppActionMenu'
import AppDropdown from '../../components/AppDropdown'
import AppPagination from '../../components/AppPagination'
import { guardCreateAction } from '../../utils/permissionActionGuard'
import {
  fetchReportScheduleOptions,
  listReportSchedules,
  updateReportScheduleStatus,
} from './api/reportsApi'
import { recurrenceLabel } from './components/ReportScheduleForm'

const STATUS_FILTERS = [
  { value: 'all', label: 'All Schedules' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
]

export default function ReportSchedulesListScreen() {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [moduleOptions, setModuleOptions] = useState([{ value: '', label: 'All Modules' }])
  const [filters, setFilters] = useState({
    search: '',
    module: '',
    status: 'all',
  })

  const totalPages = useMemo(() => {
    if (!total) return 1
    return Math.max(1, Math.ceil(total / pageSize))
  }, [pageSize, total])

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  async function loadModuleOptions() {
    try {
      const data = await fetchReportScheduleOptions()
      const next = [{ value: '', label: 'All Modules' }]
      for (const module of data?.modules || []) {
        next.push({
          value: module.id,
          label: module.id.replaceAll('-', ' ').toUpperCase(),
        })
      }
      setModuleOptions(next)
    } catch {
      setModuleOptions([{ value: '', label: 'All Modules' }])
    }
  }

  async function loadRows(nextPage = page) {
    setLoading(true)
    setError('')
    try {
      const includeArchived = filters.status === 'archived' || filters.status === 'all'
      const data = await listReportSchedules({
        page: nextPage,
        pageSize,
        includeTotal: true,
        includeArchived,
        module: filters.module || undefined,
        search: filters.search || undefined,
      })
      let items = Array.isArray(data?.items) ? data.items : []
      if (filters.status === 'active') {
        items = items.filter((item) => !item.isArchive && item.isActive)
      } else if (filters.status === 'paused') {
        items = items.filter((item) => !item.isArchive && !item.isActive)
      } else if (filters.status === 'archived') {
        items = items.filter((item) => item.isArchive)
      }
      setRows(items)
      setTotal(Number(data?.total || 0))
      setHasMore(Boolean(data?.hasMore))
    } catch (requestError) {
      setRows([])
      setError(requestError?.response?.data?.detail?.message || 'Failed to load schedules.')
    } finally {
      setLoading(false)
    }
  }

  async function applyStatus(row, action) {
    setMessage('')
    setError('')
    if (String(action || '').toUpperCase() === 'RESUME' && String(row?.recurrenceType || '').toUpperCase() === 'ONE_TIME') {
      setError('One-time schedule cannot be resumed. Please edit run date/time.')
      return
    }
    try {
      await updateReportScheduleStatus(row.id, action)
      setMessage('Schedule updated.')
      window.dispatchEvent(new Event('report-schedules-updated'))
      await loadRows(page)
    } catch (requestError) {
      setError(requestError?.response?.data?.detail?.message || 'Failed to update schedule status.')
    }
  }

  useEffect(() => {
    loadModuleOptions()
  }, [])

  useEffect(() => {
    loadRows(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.module, filters.status])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadRows(1)
    }, 300)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search])

  useEffect(() => {
    function refresh() {
      loadRows(page)
    }
    window.addEventListener('report-schedules-updated', refresh)
    return () => window.removeEventListener('report-schedules-updated', refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  return (
    <div className="px-8 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        {/* <div>
          <h1 className="font-headline text-3xl font-extrabold text-primary">Schedule Reports</h1>
          <p className="mt-1 text-sm text-on-surface-variant">Manage all report dispatch schedules.</p>
        </div> */}
        <div>
            <h1 className="font-headline text-5xl font-extrabold text-on-surface tracking-tight mb-2">
              Schedule Reports
            </h1>
            <p className="text-on-surface-variant text-lg max-w-xl">
              Manage all report dispatch schedules.
            </p>
          </div>
        <button
          type="button"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-on-primary"
          onClick={() => {
            const guardResult = guardCreateAction('/reports/schedules/entry', 'Reports / Schedules')
            if (!guardResult.allowed) return
            navigate('/reports/schedules/entry')
          }}
        >
          Create Schedule
        </button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl bg-surface-container-low p-4 md:grid-cols-3">
        <input
          type="text"
          value={filters.search}
          onChange={(event) => setFilter('search', event.target.value)}
          placeholder="Search schedules..."
          className="w-full rounded-xl border border-outline-variant/25 bg-surface-container-lowest px-3 py-2.5 text-sm"
        />
        <AppDropdown
          value={filters.module}
          onChange={(value) => setFilter('module', String(value))}
          options={moduleOptions}
        />
        <AppDropdown
          value={filters.status}
          onChange={(value) => setFilter('status', String(value))}
          options={STATUS_FILTERS}
        />
      </div>

      {error ? <p className="mb-3 text-sm text-error">{error}</p> : null}
      {message ? <p className="mb-3 text-sm text-primary">{message}</p> : null}

      <div className="overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 border-b border-surface-container bg-surface-container px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-on-surface-variant">
          <span>Report</span>
          <span>Module</span>
          <span>Next Run</span>
          <span>Status</span>
          <span className="text-right">Action</span>
        </div>

        {loading ? (
          <p className="px-4 py-8 text-sm text-on-surface-variant">Loading schedules...</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-8 text-sm text-on-surface-variant">No schedules found.</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 border-b border-surface-container/60 px-4 py-3 text-sm">
              <div>
                <p className="font-bold text-on-surface">{row.reportTitle || row.reportKey}</p>
                <p className="text-xs text-on-surface-variant">{recurrenceLabel(row.recurrenceType)}</p>
              </div>
              <span className="text-on-surface-variant">{row.module}</span>
              <span className="text-on-surface-variant">{row.nextRunAt ? new Date(row.nextRunAt).toLocaleString() : '-'}</span>
              <span className="text-on-surface-variant">{row.isArchive ? 'Archived' : row.isActive ? 'Active' : 'Paused'}</span>
              <div className="flex justify-end">
                <AppActionMenu
                  triggerLabel=""
                  triggerIcon="more_vert"
                  showChevron={false}
                  triggerClassName="h-8 w-8 rounded-md bg-transparent p-0 text-on-surface-variant hover:bg-surface-container-high"
                  items={[
                    { key: 'edit', label: 'Edit', icon: 'edit' },
                    row.isActive
                      ? { key: 'pause', label: 'Pause', icon: 'pause' }
                      : { key: 'resume', label: 'Resume', icon: 'play_arrow' },
                    row.isArchive
                      ? { key: 'unarchive', label: 'Unarchive', icon: 'unarchive' }
                      : { key: 'archive', label: 'Archive', icon: 'archive' },
                  ]}
                  onSelect={(key) => {
                    if (key === 'edit') {
                      navigate(`/reports/schedules/entry?id=${row.id}`, { state: { schedule: row } })
                    }
                    if (key === 'pause') applyStatus(row, 'PAUSE')
                    if (key === 'resume') applyStatus(row, 'RESUME')
                    if (key === 'archive') applyStatus(row, 'ARCHIVE')
                    if (key === 'unarchive') applyStatus(row, 'UNARCHIVE')
                  }}
                />
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-3">
        <AppPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          canPrevious={page > 1}
          canNext={page < totalPages || Boolean(hasMore)}
        />
      </div>
    </div>
  )
}
